import { Controller } from '@hotwired/stimulus'

const STATUS_CONFIG = {
  online:       { label: 'Online',              dotClass: 'bg-green-500' },
  paused:       { label: 'Pausado',             dotClass: 'bg-yellow-500' },
  off_schedule: { label: 'Fora do expediente',  dotClass: 'bg-gray-400' },
  inactive:     { label: 'Inativo',             dotClass: 'bg-orange-400' }
}

const PAUSE_LABELS = {
  lunch: 'Almoço', meeting: 'Reunião', dinner: 'Jantar', other: 'Outro'
}

export default class extends Controller {
  static targets = [
    'menu', 'indicator', 'label', 'chevron',
    'statusDisplay', 'statusDot', 'statusText', 'statusDetail',
    'pauseAction', 'unpauseAction',
    'overrideAction', 'cancelOverrideAction',
    'pausePicker', 'overridePicker', 'confirmPauseBtn', 'customDurationInput',
    'confirmOverrideBtn', 'customOverrideInput'
  ]

  static values = {
    status: { type: String, default: 'online' },
    source: { type: String, default: 'schedule' },
    reason: { type: String, default: '' },
    pauseId: { type: String, default: '' }
  }

  connect() {
    this.isOpen = false
    this.selectedReason = null
    this.selectedDuration = null
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this.fetchStatus()
    this.startPolling()
  }

  disconnect() {
    this.stopPolling()
    document.removeEventListener('click', this.closeOnOutsideClick)
    document.removeEventListener('touchstart', this.closeOnOutsideClick)
  }

  // --- Polling ---

  startPolling() {
    this.pollTimer = setInterval(() => this.fetchStatus(), 30000)
  }

  stopPolling() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  }

  // --- API calls ---

  async fetchStatus() {
    try {
      const data = await this.apiGet('/api/v1/user_status')
      this.applyStatus(data)
    } catch (e) {
      console.error('[UserStatus] fetch failed:', e)
    }
  }

  async confirmPause() {
    if (!this.selectedReason || !this.selectedDuration) return

    try {
      const data = await this.apiPost('/api/v1/user_status/pause', {
        pause: {
          reason: PAUSE_LABELS[this.selectedReason] || this.selectedReason,
          pause_type: this.selectedReason,
          duration_minutes: this.selectedDuration
        }
      })
      this.applyStatus(data)
      this.hidePausePicker()
      this.close()
    } catch (e) {
      console.error('[UserStatus] pause failed:', e)
    }
  }

  async unpause() {
    try {
      const data = await this.apiDelete('/api/v1/user_status/pause')
      this.applyStatus(data)
      this.close()
    } catch (e) {
      console.error('[UserStatus] unpause failed:', e)
    }
  }

  selectOverrideDuration(event) {
    this.selectedOverrideDuration = event.params.minutes
    this.highlightOverrideSelection(event.currentTarget)
    if (this.hasCustomOverrideInputTarget) {
      this.customOverrideInputTarget.value = ''
      this.customOverrideInputTarget.classList.remove('border-blue-400', 'bg-blue-100')
    }
    this.updateConfirmOverrideBtn()
  }

  selectCustomOverrideDuration() {
    const value = parseInt(this.customOverrideInputTarget.value, 10)
    if (value > 0 && value <= 480) {
      this.selectedOverrideDuration = value
      this.clearOverridePresetHighlights()
      this.customOverrideInputTarget.classList.add('border-blue-400', 'bg-blue-100')
    } else {
      this.selectedOverrideDuration = null
      this.customOverrideInputTarget.classList.remove('border-blue-400', 'bg-blue-100')
    }
    this.updateConfirmOverrideBtn()
  }

  highlightOverrideSelection(btn) {
    this.clearOverridePresetHighlights()
    btn.classList.add('bg-blue-100', 'border-blue-400')
  }

  clearOverridePresetHighlights() {
    if (!this.hasOverridePickerTarget) return
    this.overridePickerTarget.querySelectorAll('[data-user-status-minutes-param]').forEach(b => {
      b.classList.remove('bg-blue-100', 'border-blue-400')
    })
  }

  updateConfirmOverrideBtn() {
    if (!this.hasConfirmOverrideBtnTarget) return
    this.confirmOverrideBtnTarget.disabled = !this.selectedOverrideDuration
  }

  async confirmOverride() {
    if (!this.selectedOverrideDuration) return

    try {
      const data = await this.apiPost('/api/v1/user_status/override', {
        override: { duration_minutes: this.selectedOverrideDuration }
      })
      this.applyStatus(data)
      this.hideOverridePicker()
      this.close()
    } catch (e) {
      console.error('[UserStatus] override failed:', e)
    }
  }

  async cancelOverride() {
    try {
      const data = await this.apiDelete('/api/v1/user_status/override')
      this.applyStatus(data)
      this.close()
    } catch (e) {
      console.error('[UserStatus] cancel override failed:', e)
    }
  }

  // --- Status rendering ---

  applyStatus(data) {
    const status = data.status
    const source = data.source
    const reason = data.reason
    const pauseId = data.pause?.id

    this.statusValue = status
    this.sourceValue = source || ''
    this.reasonValue = reason || ''
    this.pauseIdValue = pauseId ? String(pauseId) : ''

    const config = STATUS_CONFIG[status]
    if (!config) return

    // Update indicator dot
    if (this.hasIndicatorTarget) {
      Object.values(STATUS_CONFIG).forEach(c => this.indicatorTarget.classList.remove(c.dotClass))
      this.indicatorTarget.classList.add(config.dotClass)
    }

    // Update label under name
    if (this.hasLabelTarget) {
      this.labelTarget.textContent = reason && status === 'paused'
        ? `${config.label} — ${reason}`
        : config.label
    }

    // Update dropdown status display
    if (this.hasStatusDotTarget) {
      Object.values(STATUS_CONFIG).forEach(c => this.statusDotTarget.classList.remove(c.dotClass))
      this.statusDotTarget.classList.add(config.dotClass)
    }
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = config.label
    if (this.hasStatusDetailTarget) {
      if (reason && status === 'paused') {
        this.statusDetailTarget.textContent = reason
        this.statusDetailTarget.classList.remove('hidden')
      } else {
        this.statusDetailTarget.classList.add('hidden')
      }
    }

    this.updateActions(status, source)
    this.dispatchStatusChange(status)
  }

  updateActions(status, source) {
    const show = (target) => { if (target) target.style.display = '' }
    const hide = (target) => { if (target) target.style.display = 'none' }

    // Hide all actions first
    ;[
      this.hasPauseActionTarget && this.pauseActionTarget,
      this.hasUnpauseActionTarget && this.unpauseActionTarget,
      this.hasOverrideActionTarget && this.overrideActionTarget,
      this.hasCancelOverrideActionTarget && this.cancelOverrideActionTarget
    ].forEach(t => hide(t))

    // No actions for users without a schedule or inactive (heartbeat will auto-recover)
    if (source === 'no_schedule' || status === 'inactive') return

    if (status === 'online' && source === 'override') {
      show(this.cancelOverrideActionTarget)
    } else if (status === 'online') {
      show(this.pauseActionTarget)
    } else if (status === 'paused') {
      show(this.unpauseActionTarget)
    } else if (status === 'off_schedule') {
      show(this.overrideActionTarget)
    }
  }

  dispatchStatusChange(status) {
    window.dispatchEvent(new CustomEvent('user-status:changed', {
      detail: { status, source: this.sourceValue, reason: this.reasonValue }
    }))
  }

  // --- Pause picker ---

  showPausePicker() {
    this.selectedReason = null
    this.selectedDuration = null
    this.updateConfirmBtn()
    this.clearSelections()

    if (this.hasPauseActionTarget) this.pauseActionTarget.style.display = 'none'
    if (this.hasPausePickerTarget) this.pausePickerTarget.style.display = ''
  }

  hidePausePicker() {
    if (this.hasPausePickerTarget) this.pausePickerTarget.style.display = 'none'
    this.updateActions(this.statusValue, this.sourceValue)
  }

  selectReason(event) {
    this.selectedReason = event.params.reason
    this.highlightSelection(event.currentTarget, 'reason')
    this.updateConfirmBtn()
  }

  selectDuration(event) {
    this.selectedDuration = event.params.minutes
    this.highlightSelection(event.currentTarget, 'duration')
    if (this.hasCustomDurationInputTarget) {
      this.customDurationInputTarget.value = ''
      this.customDurationInputTarget.classList.remove('border-yellow-400', 'bg-yellow-100')
    }
    this.updateConfirmBtn()
  }

  selectCustomDuration() {
    const value = parseInt(this.customDurationInputTarget.value, 10)
    if (value > 0 && value <= 480) {
      this.selectedDuration = value
      // Clear preset button highlights
      const container = this.customDurationInputTarget.closest('[data-user-status-target="pausePicker"]')
      if (container) {
        container.querySelectorAll('[data-user-status-minutes-param]').forEach(b => {
          b.classList.remove('bg-yellow-100', 'border-yellow-400')
        })
      }
      this.customDurationInputTarget.classList.add('border-yellow-400', 'bg-yellow-100')
    } else {
      this.selectedDuration = null
      this.customDurationInputTarget.classList.remove('border-yellow-400', 'bg-yellow-100')
    }
    this.updateConfirmBtn()
  }

  highlightSelection(btn, group) {
    const container = btn.closest('[data-user-status-target="pausePicker"]')
    if (!container) return

    const selector = group === 'reason'
      ? '[data-user-status-reason-param]'
      : '[data-user-status-minutes-param]'

    container.querySelectorAll(selector).forEach(b => {
      b.classList.remove('bg-yellow-100', 'border-yellow-400')
    })
    btn.classList.add('bg-yellow-100', 'border-yellow-400')
  }

  clearSelections() {
    if (!this.hasPausePickerTarget) return
    this.pausePickerTarget.querySelectorAll('button[data-user-status-reason-param], button[data-user-status-minutes-param]').forEach(b => {
      b.classList.remove('bg-yellow-100', 'border-yellow-400')
    })
    if (this.hasCustomDurationInputTarget) {
      this.customDurationInputTarget.value = ''
      this.customDurationInputTarget.classList.remove('border-yellow-400', 'bg-yellow-100')
    }
  }

  updateConfirmBtn() {
    if (!this.hasConfirmPauseBtnTarget) return
    this.confirmPauseBtnTarget.disabled = !(this.selectedReason && this.selectedDuration)
  }

  // --- Override picker ---

  showOverridePicker() {
    this.selectedOverrideDuration = null
    this.clearOverridePresetHighlights()
    if (this.hasCustomOverrideInputTarget) {
      this.customOverrideInputTarget.value = ''
      this.customOverrideInputTarget.classList.remove('border-blue-400', 'bg-blue-100')
    }
    this.updateConfirmOverrideBtn()

    if (this.hasOverrideActionTarget) this.overrideActionTarget.style.display = 'none'
    if (this.hasOverridePickerTarget) this.overridePickerTarget.style.display = ''
  }

  hideOverridePicker() {
    if (this.hasOverridePickerTarget) this.overridePickerTarget.style.display = 'none'
    this.updateActions(this.statusValue, this.sourceValue)
  }

  // --- Dropdown menu ---

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.isOpen ? this.close() : this.open()
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true

    const menu = this.menuTarget
    menu.style.display = 'block'
    menu.style.opacity = '0'
    menu.style.transform = 'translateY(8px)'
    menu.offsetHeight
    menu.style.transition = 'all 0.15s ease-out'
    menu.style.opacity = '1'
    menu.style.transform = 'translateY(0)'

    if (this.hasChevronTarget) this.chevronTarget.style.transform = 'rotate(180deg)'

    // Reset pickers when opening
    this.hidePausePicker()
    this.hideOverridePicker()

    setTimeout(() => {
      document.addEventListener('click', this.closeOnOutsideClick)
      document.addEventListener('touchstart', this.closeOnOutsideClick)
    }, 100)
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false

    const menu = this.menuTarget
    menu.style.transition = 'all 0.15s ease-in'
    menu.style.opacity = '0'
    menu.style.transform = 'translateY(8px)'

    setTimeout(() => {
      if (!this.isOpen) menu.style.display = 'none'
    }, 150)

    if (this.hasChevronTarget) this.chevronTarget.style.transform = ''

    document.removeEventListener('click', this.closeOnOutsideClick)
    document.removeEventListener('touchstart', this.closeOnOutsideClick)
  }

  closeOnOutsideClick(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }

  openProfileModal() {
    this.close()
    document.getElementById('profile_edit_modal')?.showModal()
  }

  // --- HTTP helpers ---

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }

  async apiGet(path) {
    const response = await fetch(path, {
      headers: { 'Accept': 'application/json' }
    })
    if (!response.ok) throw new Error(`GET ${path} failed: ${response.status}`)
    return response.json()
  }

  async apiPost(path, body) {
    const response = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error(`POST ${path} failed: ${response.status}`)
    return response.json()
  }

  async apiDelete(path) {
    const response = await fetch(path, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': this.csrfToken
      }
    })
    if (!response.ok) throw new Error(`DELETE ${path} failed: ${response.status}`)
    return response.json()
  }
}
